/**
 * Size guide controller for the product page modal.
 *
 * This script turns static size-chart HTML pasted into Shopify pages into an
 * interactive UI that can:
 * - switch the active measurement context (`talla`, `pecho`, `cintura`, `cadera`)
 * - update the helper copy and silhouette overlays
 * - keep track of a selected size independently from the active measure
 * - sync the chosen size back into the product variant picker
 * - close the surrounding dialog when the CTA is used
 *
 * Expected HTML contract:
 * - `[data-size-chart]` marks a chart root
 * - `[data-measure-trigger]` changes the active measure
 * - `[data-size-only-selection]` selects a size without changing the default measure state
 * - `[data-size-selection][data-size-measure]` selects a size and activates a measure
 * - `[data-measure-copy]` provides copy for each measure
 * - `[data-measure-figure]` maps silhouette overlays to a measure
 * - `[data-size-chart-action]` is the footer CTA
 */
class SizeChartController {
  /**
   * Builds a controller instance for a single size chart root.
   *
   * The constructor caches all relevant DOM hooks and immediately renders the
   * default state so the chart behaves consistently even when injected into the
   * DOM after page load.
   *
   * @param {HTMLElement} root
   */
  constructor(root) {
    /** @type {HTMLElement} */
    this.root = root;
    /** @type {HTMLElement | null} */
    this.copyTarget = root.querySelector('[data-size-chart-copy]');
    /** @type {HTMLButtonElement | null} */
    this.actionButton = root.querySelector('[data-size-chart-action]');
    /** @type {HTMLButtonElement[]} */
    this.measureButtons = Array.from(/** @type {NodeListOf<HTMLButtonElement>} */(root.querySelectorAll('[data-measure-trigger]')));
    /** @type {HTMLButtonElement[]} */
    this.sizeOnlyButtons = Array.from(
      /** @type {NodeListOf<HTMLButtonElement>} */(root.querySelectorAll('[data-size-only-selection]'))
    );
    /** @type {HTMLButtonElement[]} */
    this.cellButtons = Array.from(
      /** @type {NodeListOf<HTMLButtonElement>} */(root.querySelectorAll('[data-size-selection][data-size-measure]'))
    );
    /** @type {HTMLElement[]} */
    this.figureOverlays = Array.from(/** @type {NodeListOf<HTMLElement>} */(root.querySelectorAll('[data-measure-figure]')));
    /** @type {Map<string | undefined, string>} */
    this.measureCopyMap = new Map(
      Array.from(/** @type {NodeListOf<HTMLElement>} */(root.querySelectorAll('[data-measure-copy]'))).map((node) => [
        node.dataset.measureCopy,
        node.textContent?.trim() || '',
      ])
    );

    /** @type {string} */
    this.defaultMeasure = root.dataset.defaultMeasure || 'talla';
    /** @type {string} */
    this.defaultCopy = root.dataset.defaultCopy || 'Seleccione una medida para ver más detalles.';
    /** @type {string} */
    this.defaultCta = root.dataset.defaultCta || 'Cerrar';
    /** @type {string} */
    this.selectedSize = '';
    /** @type {string} */
    this.activeMeasure = this.defaultMeasure;

    this.handleClick = this.handleClick.bind(this);
    this.root.addEventListener('click', this.handleClick);

    this.resetState();
  }

  /**
   * Central click delegation handler for the chart.
   *
   * Order matters here:
   * 1. Header measure buttons
   * 2. Size-only buttons in the first column
   * 3. Measure-specific range cells
   * 4. Footer CTA
   *
   * This keeps the HTML simple and avoids wiring listeners for each cell.
   *
   * @param {MouseEvent} event
   */
  handleClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const measureButton = target.closest('[data-measure-trigger]');
    if (measureButton instanceof HTMLButtonElement) {
      this.handleMeasureTrigger(measureButton.dataset.measureTrigger || this.defaultMeasure);
      return;
    }

    const sizeOnlyButton = target.closest('[data-size-only-selection]');
    if (sizeOnlyButton instanceof HTMLButtonElement) {
      this.handleSizeOnlySelection(sizeOnlyButton.dataset.sizeOnlySelection || '');
      return;
    }

    const cellButton = target.closest('[data-size-selection][data-size-measure]');
    if (cellButton instanceof HTMLButtonElement) {
      this.handleSizeSelection(
        cellButton.dataset.sizeSelection || '',
        cellButton.dataset.sizeMeasure || this.defaultMeasure
      );
      return;
    }

    const actionButton = target.closest('[data-size-chart-action]');
    if (actionButton instanceof HTMLButtonElement) {
      this.handleAction();
    }
  }

  /**
   * Restores the visual default state of the chart.
   *
   * This resets the active measure back to `talla`, clears any previously
   * selected size, restores the default helper copy and returns the CTA to its
   * close-only mode.
   */
  resetState() {
    this.activeMeasure = this.defaultMeasure;
    this.selectedSize = '';
    this.render();
  }

  /**
   * Activates a header measure.
   *
   * Selecting `talla` is treated as a full reset. Any other measure keeps the
   * currently selected size, if one exists, while changing the active visual
   * context used by the copy and figure overlays.
   *
   * @param {string} measure
   */
  handleMeasureTrigger(measure) {
    if (measure === 'talla') {
      this.resetState();
      return;
    }

    this.activeMeasure = measure;
    this.render();
  }

  /**
   * Selects a size from the first column without activating a measurement.
   *
   * This mirrors the desired UX where clicking `M` should keep the chart in its
   * default explanatory state while still preparing the CTA to add that size to
   * the product form.
   *
   * @param {string} size
   */
  handleSizeOnlySelection(size) {
    this.selectedSize = size;
    this.activeMeasure = this.defaultMeasure;
    this.render();
  }

  /**
   * Selects a size from a measurement cell and activates that measure.
   *
   * Example: clicking `82 - 98` in the `cadera` column should both select size
   * `S` and switch the chart into the `cadera` visual state.
   *
   * @param {string} size
   * @param {string} measure
   */
  handleSizeSelection(size, measure) {
    this.selectedSize = size;
    this.activeMeasure = measure;
    this.render();
  }

  /**
   * Handles the footer CTA action.
   *
   * When a size is selected, the CTA attempts to sync that size into the
   * product variant picker before closing the dialog. If no size is selected,
   * it behaves like a simple close button.
   */
  handleAction() {
    if (this.selectedSize) {
      const didSelect = this.selectVariantSize(this.selectedSize);
      if (didSelect) {
        this.closeDialog();
        return;
      }
    }

    this.closeDialog();
  }

  /**
   * Applies controller state back to the DOM.
   *
   * This method is responsible for:
   * - active/inactive state on measure headings
   * - active/inactive state on size-only buttons
   * - active/inactive state on measurement cells
   * - helper copy updates
   * - CTA label and action mode updates
   */
  render() {
    for (const button of this.measureButtons) {
      const isActive = button.dataset.measureTrigger === this.activeMeasure;
      button.dataset.active = String(isActive);
      button.setAttribute('aria-pressed', String(isActive));
    }

    for (const button of this.sizeOnlyButtons) {
      const isActive = button.dataset.sizeOnlySelection === this.selectedSize && this.activeMeasure === this.defaultMeasure;
      button.dataset.active = String(isActive);
      button.setAttribute('aria-pressed', String(isActive));
    }

    for (const overlay of this.figureOverlays) {
      const isActive = overlay.dataset.measureFigure === this.activeMeasure;
      overlay.dataset.active = String(isActive);
    }

    for (const button of this.cellButtons) {
      const isActive =
        button.dataset.sizeSelection === this.selectedSize && button.dataset.sizeMeasure === this.activeMeasure;
      button.dataset.active = String(isActive);
      button.setAttribute('aria-pressed', String(isActive));
    }

    if (this.copyTarget) {
      if (this.activeMeasure === 'talla') {
        this.copyTarget.textContent = this.defaultCopy;
      } else {
        this.copyTarget.textContent = this.measureCopyMap.get(this.activeMeasure) || this.defaultCopy;
      }
    }

    if (this.actionButton) {
      const isSelectionMode = Boolean(this.selectedSize);
      this.actionButton.dataset.actionMode = isSelectionMode ? 'select-size' : 'close';
      this.actionButton.textContent = isSelectionMode
        ? `SELECCIONAR TALLA ${this.selectedSize}`
        : this.defaultCta;
    }
  }

  /**
   * Finds and activates the corresponding radio input in the surrounding
   * variant picker.
   *
   * The chart lives inside the size-guide dialog, which itself sits within the
   * same fieldset as the size radio buttons. Clicking the input is preferred
   * over mutating state manually because it reuses the theme's existing variant
   * picker logic and events.
   *
   * @param {string} size
   * @returns {boolean}
   */
  selectVariantSize(size) {
    const fieldset = this.root.closest('.variant-option--size-guide');
    if (!(fieldset instanceof HTMLElement)) return false;

    const escapedSize = window.CSS && typeof window.CSS.escape === 'function' ? window.CSS.escape(size) : size;
    const input = fieldset.querySelector(`input[type="radio"][value="${escapedSize}"]`);

    if (!(input instanceof HTMLInputElement) || input.disabled || input.getAttribute('aria-disabled') === 'true') {
      return false;
    }

    input.click();
    return true;
  }

  /**
   * Closes the nearest wrapping `dialog-component`, if present.
   *
   * The chart itself does not own the modal lifecycle, so it only delegates to
   * the dialog wrapper when available.
   */
  closeDialog() {
    const dialogComponent = /** @type {{ closeDialog?: () => void } | null} */ (this.root.closest('dialog-component'));
    if (dialogComponent && typeof dialogComponent.closeDialog === 'function') {
      dialogComponent.closeDialog();
    }
  }
}

/**
 * Initializes any size charts that have not been bootstrapped yet.
 *
 * This is safe to call multiple times because initialized charts are marked
 * with `data-size-chart-ready`.
 *
 * @param {ParentNode} root
 */
function initializeCharts(root = document) {
  const charts = root.querySelectorAll('[data-size-chart]:not([data-size-chart-ready])');
  for (const chart of charts) {
    if (!(chart instanceof HTMLElement)) continue;
    chart.dataset.sizeChartReady = 'true';
    new SizeChartController(chart);
  }
}

initializeCharts();

/**
 * Watches for chart markup injected after initial page load.
 *
 * This matters because the size guide content can be re-rendered or inserted
 * dynamically when dialogs open or product sections morph.
 */
const observer = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (!(node instanceof HTMLElement)) continue;

      if (node.matches('[data-size-chart]')) {
        initializeCharts(node.parentNode || document);
      } else if (node.querySelector('[data-size-chart]')) {
        initializeCharts(node);
      }
    }
  }
});

observer.observe(document.body, {
  childList: true,
  subtree: true,
});
